import React from 'react';
import { LegalLayout, Section, P, UL, LI, B } from '../components/LegalLayout';

const Terms = () => (
  <LegalLayout
    title="Terms of Service"
    effectiveDate="May 28, 2026"
    seoTitle="Terms of Service, CoupleIn"
    seoDescription="The agreement between you and Couple In LTD when you use the CoupleIn app. Subscription, refunds, acceptable use, and liability."
    path="/terms"
  >
    <Section number="1" title="Agreement">
      <P>
        These Terms form a binding agreement between you and <B>Couple In LTD</B>, a company registered in England and Wales (company number 16342045), registered office 54 Dunloe Street, London E2 8EN, United Kingdom ("we", "us"). By creating an account or using CoupleIn (the "App") you accept these Terms and our <a href="/privacy" className="text-pink-600 underline">Privacy Policy</a>. If you do not accept them, do not use the App.
      </P>
    </Section>

    <Section number="2" title="Eligibility">
      <P>
        You must be at least 17 years old to use the App. By using CoupleIn you confirm you meet this age requirement and that you can form a legally binding contract in your country of residence.
      </P>
    </Section>

    <Section number="3" title="Your account">
      <P>
        You sign in with Google or Apple. You are responsible for keeping the linked account secure. Notify <a href="mailto:info@coupleinapp.com" className="text-pink-600 underline">info@coupleinapp.com</a> immediately if you suspect unauthorised use. You may delete your account at any time from Settings → Delete Profile, or by emailing us.
      </P>
    </Section>

    <Section number="4" title="Partner connection">
      <P>
        CoupleIn is designed for two consenting adults in a relationship. By connecting with a partner, you agree that data marked as shared (moods, gestures, points, calendar events, conflict sessions) will be visible to that partner. You can disconnect at any time, which removes their access to your shared data going forward.
      </P>
      <P>
        You are responsible for the content you share. Do not share another person's personal data without their consent.
      </P>
    </Section>

    <Section number="5" title="Acceptable use">
      <P>You agree not to:</P>
      <UL>
        <LI>use the App for any unlawful purpose, harassment, abuse, or to threaten anyone;</LI>
        <LI>upload content that is hateful, defamatory, sexually explicit involving minors, or that infringes another person's rights;</LI>
        <LI>impersonate any person or misrepresent your relationship status;</LI>
        <LI>attempt to reverse engineer, scrape, decompile, or interfere with the App or our servers;</LI>
        <LI>use automated systems to access the App except as permitted by our API;</LI>
        <LI>circumvent rate-limits, paywalls, ads or other technical protections.</LI>
      </UL>
      <P>
        We may suspend or terminate accounts that breach these rules, including immediate removal for conduct that puts another user at risk.
      </P>
    </Section>

    <Section number="6" title="Subscriptions and payments">
      <P>
        CoupleIn offers a free 7-day trial. After trial, free-tier users can keep using the core features with usage limits (e.g. 1 gesture). To remove limits and access Premium features (unlimited gestures, reorder rewards, ad-free), you may subscribe via Apple App Store or Google Play in-app purchase.
      </P>
      <P>
        <B>Billing:</B> Subscriptions auto-renew through the platform you purchased them on. Pricing and renewal periods are shown on the upgrade screen at the moment of purchase. Payment is charged to your Apple/Google account. We never see your payment card details.
      </P>
      <P>
        <B>Cancellation &amp; refunds:</B> Cancel any time via Apple ID Subscriptions or Google Play Subscriptions. Refunds are handled by Apple/Google under their respective policies. Statutory rights under UK consumer law (Consumer Rights Act 2015, Consumer Contracts Regulations 2013) apply where the platform refund flow is unable to assist.
      </P>
    </Section>

    <Section number="7" title="Trial extension">
      <P>
        Eligible users may extend their free trial by 7 additional days (14 total) once per account. The extension does not require payment and does not auto-convert to a paid subscription.
      </P>
    </Section>

    <Section number="8" title="Browny points and gestures">
      <P>
        Browny points are virtual in-app credits with <B>no monetary value</B>. They cannot be exchanged for cash, purchased separately, transferred between accounts, or redeemed outside the App. We may adjust earning or spending rules to prevent abuse.
      </P>
    </Section>

    <Section number="9" title="Ads">
      <P>
        Non-premium users may see one short interstitial advertisement per day before revealing their partner's mood. Premium users do not see ads. Ads are delivered by Google AdMob in line with their content policy. Report inappropriate ads to <a href="mailto:info@coupleinapp.com" className="text-pink-600 underline">info@coupleinapp.com</a>.
      </P>
    </Section>

    <Section number="10" title="Notifications">
      <P>
        We send transactional and engagement push notifications. You can manage them in Settings → Notifications or revoke push permission entirely in your device OS settings. Some notifications related to your account (e.g. trial ending) are essential and may still be delivered as in-app notifications.
      </P>
    </Section>

    <Section number="11" title="Intellectual property">
      <P>
        The App, branding, name "CoupleIn" and all software are owned by Couple In LTD and protected by intellectual-property laws. You receive a limited, personal, non-exclusive, non-transferable licence to use the App for its intended purpose.
      </P>
      <P>
        You retain ownership of content you create (moods, gestures, etc.). By creating it in the App you grant us a worldwide, royalty-free licence to host, store and transmit that content solely to operate the service for you and your partner.
      </P>
    </Section>

    <Section number="12" title="Disclaimers">
      <P>
        CoupleIn is a relationship-wellness tool. It is <B>not</B> a substitute for professional counselling, therapy or medical advice. We make no guarantees about relationship outcomes. The App is provided "as is" without warranties to the maximum extent permitted by law.
      </P>
    </Section>

    <Section number="13" title="Limitation of liability">
      <P>
        Nothing in these Terms limits our liability for death or personal injury caused by negligence, for fraud, or for any other liability that cannot be excluded by UK law. Subject to that, our total aggregate liability for any claim arising out of or relating to the App is limited to the greater of (a) the amount you paid us in the 12 months preceding the claim or (b) £50.
      </P>
      <P>
        We are not liable for indirect, consequential, or punitive damages, or for loss of profits, data, relationships, or goodwill.
      </P>
    </Section>

    <Section number="14" title="Termination">
      <P>
        You can stop using the App at any time and delete your account. We can suspend or terminate your access if you breach these Terms, if required by law, or if continued provision is no longer commercially viable. On termination, sections that by nature should survive will survive (intellectual property, disclaimers, liability, dispute resolution).
      </P>
    </Section>

    <Section number="15" title="Changes to the service or Terms">
      <P>
        We may add, change or remove features to improve the App or comply with law. For material changes to these Terms, we will notify you in-app or by email at least 7 days before they take effect. If you do not accept the changes you must stop using the App.
      </P>
    </Section>

    <Section number="16" title="Governing law and disputes">
      <P>
        These Terms are governed by the laws of England and Wales. The courts of England and Wales have exclusive jurisdiction, except that consumers may be entitled to bring proceedings in the courts of their country of residence under local mandatory law.
      </P>
    </Section>

    <Section number="17" title="Contact">
      <P>
        <B>Couple In LTD</B><br />
        54 Dunloe Street, London E2 8EN, United Kingdom<br />
        Email: <a href="mailto:info@coupleinapp.com" className="text-pink-600 underline">info@coupleinapp.com</a>
      </P>
    </Section>
  </LegalLayout>
);

export default Terms;
